import { useRef } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { cn } from '../../lib/cn';

export interface TabItem<V extends string> {
  readonly value: V;
  readonly label: ReactNode;
  /** Small pill after the label (People profile tabs). Hidden when undefined or 0. */
  readonly count?: number;
}

export interface TabsProps<V extends string> {
  readonly items: ReadonlyArray<TabItem<V>>;
  readonly value: V;
  readonly onChange: (value: V) => void;
  readonly label: string;
  /** Horizontal tab padding: 12px (profiles, drawers) or 14px (Money in, Finance). */
  readonly padding?: 12 | 14;
  /** Id prefix linking each tab to its panel: `${idPrefix}-tab-${value}` / `${idPrefix}-panel-${value}`. */
  readonly idPrefix?: string;
  readonly className?: string;
}

/**
 * Underline tabs (design: 40px, 13px/600, 2px navy line on the active tab, a 1px border-1 rule
 * under the strip). Arrow keys move between tabs; Home/End jump to the ends.
 */
export function Tabs<V extends string>({
  items,
  value,
  onChange,
  label,
  padding = 12,
  idPrefix,
  className,
}: TabsProps<V>) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);

  const select = (index: number) => {
    const item = items[index];
    if (!item) return;
    onChange(item.value);
    refs.current[index]?.focus();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const last = items.length - 1;
    const target =
      event.key === 'ArrowRight'
        ? (index + 1) % items.length
        : event.key === 'ArrowLeft'
          ? (index - 1 + items.length) % items.length
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? last
              : null;
    if (target === null) return;
    event.preventDefault();
    select(target);
  };

  return (
    <div
      role="tablist"
      aria-label={label}
      className={cn('flex items-center gap-0.5 border-b border-border-1', className)}
    >
      {items.map((item, index) => {
        const selected = item.value === value;
        return (
          <button
            key={item.value}
            ref={(el) => {
              refs.current[index] = el;
            }}
            type="button"
            role="tab"
            id={idPrefix ? `${idPrefix}-tab-${item.value}` : undefined}
            aria-controls={idPrefix ? `${idPrefix}-panel-${item.value}` : undefined}
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(item.value)}
            onKeyDown={(event) => onKeyDown(event, index)}
            className={cn(
              '-mb-px flex h-10 items-center gap-1.5 border-0 border-b-2 bg-transparent text-[13px] font-semibold',
              padding === 14 ? 'px-3.5' : 'px-3',
              selected ? 'border-pi-navy text-fg-1' : 'border-transparent text-fg-2',
            )}
          >
            {item.label}
            {!!item.count && ' '}
            {!!item.count && (
              <span className="rounded-pill bg-neutral-100 px-1.5 text-[11px] text-fg-2">{item.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
